import { useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Pagination, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextArea } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';
import { Panel, Rate, scopeFields, useReportFilters } from '../components/Analytics';
import { RiskEventQueue } from './RiskQueue';

// The one risk page. Tab "Chờ xử lý" (P1-D20): events the anti-abuse rules raised (he A limits, jobs/risk.ts),
// each closed as CLEARED or FRAUD. Tab "Dấu hiệu quét": screen 9 of docs/cms-golive/kpi-catalogue.md (P1-D17): every risk finding of the worker's scan
// (abnormal winners, low-turnover withdrawals, bonus abuse, shared bank / IP / device, registrations
// per IP, forced results, result edits, manual credits) is a queue row; "Đã xem" is audited. The
// summary tab has the refund rate per game; big wins keep their page.

export const RISK_LABEL: Record<string, string> = {
  abnormal_win: 'Thắng bất thường',
  low_turnover_withdrawal: 'Rút khi cược ít',
  bonus_abuse: 'Nghi lạm dụng thưởng',
  shared_bank: 'Chung tài khoản ngân hàng',
  shared_ip: 'Chung IP',
  shared_device: 'Chung thiết bị',
  reg_per_ip: 'Nhiều đăng ký một IP',
  forced_result: 'Kết quả đặt trước',
  result_edit: 'Sửa kết quả',
  manual_credit: 'Cộng tiền tay',
};

type Item = {
  id: string; kind: string; key: string; userId: number | null; userIds: number[]; amount: string | null; score: number | null;
  detail: Record<string, unknown>; at: string; ackAt: string | null; ackByName: string | null; ackNote: string | null;
  nick: string | null; nicks: { id: number; nick: string | null }[];
};
type Page = { items: Item[]; total: number; page: number; pageSize: number; open: Record<string, number> };

const pct = (v: unknown) => (typeof v === 'number' ? (v * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 }) + '%' : '-');
const n = (v: unknown) => (typeof v === 'number' ? v.toLocaleString('vi-VN', { maximumFractionDigits: 2 }) : String(v ?? '-'));

/** one plain line of what the scan found */
export function riskDetail(i: Item): string {
  const d = i.detail;
  switch (i.kind) {
    case 'abnormal_win':
      return d.game ? `${d.game}: ${n(d.bets)} lần cược, trả ${n(d.payout)} trên doanh số ${n(d.turnover)} (RTP ${pct(d.rtp)}), z = ${n(d.z)} từ ${d.from} đến ${d.to}`
        : `Thắng ròng ${n(d.netWin)} trong 7 ngày (ngưỡng ${n(d.threshold)})`;
    case 'low_turnover_withdrawal':
      return `Rút ${n(d.amount)} sau nạp ${n(d.deposit)}, mới cược ${n(d.turnoverSince)} (cần ${n(d.multiple)} lần tiền nạp)`;
    case 'bonus_abuse':
      return d.withdrawal ? `Rút ${n(d.amount)} sau thưởng ${n(d.bonus)}, mới cược ${n(d.turnoverSince)} (cần ${n(d.multiple)} lần thưởng)`
        : `Tài khoản ngân hàng ${d.account} dùng chung bởi ${(d.giftcodeRedeemers as unknown[] | undefined)?.length ?? 0} người đã nhập giftcode`;
    case 'shared_bank': return `Tài khoản ngân hàng ${d.account} dùng chung bởi ${n(d.players)} người`;
    case 'shared_ip': return `IP ${d.ip} dùng bởi ${n(d.players)} người trong ${n(d.days)} ngày`;
    case 'shared_device': return `Thiết bị ${String(d.device ?? '').slice(0, 16)}… dùng bởi ${n(d.players)} người trong ${n(d.days)} ngày`;
    case 'reg_per_ip': return `${n(d.registrations)} đăng ký từ IP ${d.ip} trong một giờ (ngưỡng ${n(d.threshold)})`;
    case 'forced_result': return `Lệnh đặt kết quả #${d.order} game ${d.game}, đã dùng ${n(d.uses)} lần${d.by ? `, người đặt ${d.by}` : ''}${d.reason ? `: ${d.reason}` : ''}`;
    case 'result_edit': return `Sửa kết quả ${d.command ?? ''} (${d.source ?? ''}), trả ${n(d.payoutBefore)} thành ${n(d.payoutAfter)}`;
    case 'manual_credit': return `Cộng tay ${d.currency === 'xu' ? 'xu' : 'KRW'}${d.wagering ? `, vòng cược ${n(d.wagering)}` : ''}`;
    default: return JSON.stringify(d);
  }
}

const KEYS = ['kind', 'state', 'userId', 'from', 'to'] as const;

function Queue() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const f = useUrlFilters(KEYS, { defaults: { state: 'open' } });
  const q = useQuery({
    queryKey: ['risk', f.values, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/risk', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const [pick, setPick] = useState<Item | null>(null);
  const [note, setNote] = useState('');
  const ack = useMutation({
    mutationFn: (i: Item) => api.post(`/api/admin/risk/${i.id}/ack`, { note: note.trim() || undefined }),
    onSuccess: () => { setPick(null); setNote(''); qc.invalidateQueries({ queryKey: ['risk'] }); },
  });
  const fields: FilterField[] = [
    { key: 'state', label: 'Trạng thái', kind: 'select', options: [['open', 'Chưa xem'], ['acked', 'Đã xem'], ['all', 'Tất cả']] },
    { key: 'kind', label: 'Loại', kind: 'select', options: [['', 'Tất cả'], ...Object.entries(RISK_LABEL).map(([k, l]) => [k, `${l}${q.data?.open[k] ? ` (${q.data.open[k]})` : ''}`] as const)] },
    { key: 'userId', label: 'Mã người chơi', kind: 'number' },
    { key: 'from', label: 'Từ ngày', kind: 'date' },
    { key: 'to', label: 'Đến ngày', kind: 'date' },
  ];
  const who = (id: number, nick: string | null) => (can('users.view') ? <Link key={id} className="cell-link" to={`/users/${id}`}>{nick ?? '#' + id}</Link> : <span key={id}>{nick ?? '#' + id}</span>);
  const columns: ColumnDef<Item, any>[] = [
    { header: 'Lúc', accessorKey: 'at', cell: (c) => <When v={c.getValue()} /> },
    { header: 'Loại', accessorKey: 'kind', cell: (c) => <Tag type={c.getValue() === 'abnormal_win' || c.getValue() === 'bonus_abuse' ? 'red' : 'purple'} size="sm">{RISK_LABEL[c.getValue()] ?? c.getValue()}</Tag> },
    { header: 'Người chơi', id: 'who', enableSorting: false, cell: (c) => {
      const i = c.row.original;
      if (i.userId) return who(i.userId, i.nick);
      return <span className="row">{i.nicks.slice(0, 6).map((u) => who(u.id, u.nick))}{i.nicks.length > 6 ? <span className="muted">+{i.nicks.length - 6}</span> : null}</span>;
    } },
    { header: 'Chi tiết', id: 'detail', enableSorting: false, cell: (c) => <span>{riskDetail(c.row.original)}</span> },
    { header: 'Số tiền (₩)', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
    { header: 'Trạng thái', id: 'st', enableSorting: false, cell: (c) => {
      const i = c.row.original;
      return i.ackAt
        ? <span><Tag type="gray" size="sm">Đã xem</Tag> <span className="muted"><When v={i.ackAt} />{i.ackNote ? `: ${i.ackNote}` : ''}</span></span>
        : can('risk.ack') ? <Button kind="ghost" size="sm" onClick={() => { setPick(i); setNote(''); ack.reset(); }}>Đánh dấu đã xem</Button> : <Tag type="magenta" size="sm">Chưa xem</Tag>;
    } },
  ];
  return (
    <>
      <FilterBar id="rk" fields={fields} filters={f} exportPath="/api/admin/risk" />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} rowKey={(r) => r.id}
        empty={{ title: f.active ? 'Không có mục nào khớp bộ lọc' : 'Không có mục rủi ro nào chưa xem', body: <>Ngưỡng nằm trong {can('settings.manage') ? <Link to="/settings">Cài đặt</Link> : 'Cài đặt'}; thắng lớn ở trang <Link to="/alerts">Cảnh báo thắng lớn</Link>.</> }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} mục`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <Modal open={!!pick} modalHeading={pick ? `Đã xem: ${RISK_LABEL[pick.kind] ?? pick.kind}` : ''} primaryButtonText="Đánh dấu đã xem" secondaryButtonText="Đóng"
        primaryButtonDisabled={ack.isPending} onRequestClose={() => setPick(null)} onRequestSubmit={() => pick && ack.mutate(pick)}>
        {pick ? <p>{riskDetail(pick)}</p> : null}
        <TextArea id="rk-note" labelText="Ghi chú (không bắt buộc)" maxCount={500} enableCounter value={note} onChange={(e) => setNote(e.target.value)} />
        <InlineError error={ack.error} />
      </Modal>
    </>
  );
}

type Summary = {
  refunds: { game: string; bet: number; refund: number; rate: number | null }[];
  bigWinsOpen: number;
};

function SummaryTab() {
  const f = useReportFilters(7);
  const q = useQuery({ queryKey: ['risk', 'summary', f.query], queryFn: () => api.get<Summary>('/api/admin/risk/summary', { from: f.query.from, to: f.query.to }) });
  const d = q.data;
  return (
    <>
      <FilterBar id="rs" fields={scopeFields(undefined, { scope: false })} filters={f} />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d ? null : (
        <>
          <p className="note"><Link to="/alerts">Thắng lớn chưa xem: {d.bigWinsOpen}</Link></p>
          <div className="split">
            <Panel title="Tỷ lệ hoàn cược theo game">
              {d.refunds.length ? (
                <table className="plain"><thead><tr><th>Game</th><th className="r">Cược</th><th className="r">Hoàn</th><th className="r">Tỷ lệ</th></tr></thead>
                  <tbody>{d.refunds.map((r) => <tr key={r.game}><td className="is-title">{r.game}</td><td className="r"><Money v={r.bet} /></td><td className="r"><Money v={r.refund} /></td><td className="r"><Rate v={r.rate} /></td></tr>)}</tbody></table>
              ) : <p className="muted">Không có cược trong khoảng.</p>}
            </Panel>
          </div>
        </>
      )}
    </>
  );
}

type RiskTab = { key: string; label: string; el: () => ReactElement };

export function RiskPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs: RiskTab[] = [
    ...(can('risk.review') ? [{ key: 'xu-ly', label: 'Chờ xử lý', el: () => <RiskEventQueue /> }] : []),
    ...(can('reports.view') ? [
      { key: 'quet', label: 'Dấu hiệu quét', el: () => <Queue /> },
      { key: 'tong-hop', label: 'Hoàn cược', el: () => <SummaryTab /> },
    ] : []),
  ];
  const tab = Math.max(0, tabs.findIndex((t) => t.key === params.get('tab')));
  // doi tab: bo loc cua tab cu (tham so URL) khong mang sang tab moi
  const pick = (i: number) => setParams(new URLSearchParams({ tab: tabs[i]?.key ?? '' }));
  return (
    <div className="page">
      <PageHeader title="Hàng chờ rủi ro" lead="Một chỗ cho mọi dấu hiệu rủi ro: mục do quy tắc chống gian lận mở (xử lý: không vi phạm hoặc gian lận) và mục worker quét mỗi phút (đánh dấu đã xem)." />
      <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => pick(selectedIndex)}>
        <TabList aria-label="Rủi ro">
          {tabs.map((t) => <Tab key={t.key}>{t.label}</Tab>)}
        </TabList>
        <TabPanels>
          {tabs.map((t, i) => <TabPanel key={t.key}>{tab === i ? t.el() : null}</TabPanel>)}
        </TabPanels>
      </Tabs>
    </div>
  );
}
